import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { centsToDisplay, dollarsToCents, todayISO } from "../lib/money.js";
import { Field, Segmented, inputCls } from "../components/ui.jsx";
import { ContactPicker } from "./Crm.jsx";

const TABS = [
  { value: "stripe", label: "Stripe" },
  { value: "import", label: "Bank import" },
  { value: "invoices", label: "Invoices" },
];

const signed = (cents) => (cents < 0 ? `−${centsToDisplay(-cents)}` : centsToDisplay(cents));

// Shared: income/expense account options from the chart of accounts.
function useAccounts() {
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    api("/api/accounts").then((d) => setAccounts(d.accounts || [])).catch(() => {});
  }, []);
  return accounts;
}

// Not a category — a DISPOSITION. "This line is not business activity at all."
// A bank's account-verification micro-deposits are the clear case: two small
// credits and a matching debit that net to zero and are neither income nor
// expense. The Ignore button has always done this; the dropdowns had no way to
// say it, so working down them left nowhere to land.
export const IGNORE = "__ignore__";

function AccountSelect({ accounts, type, value, onChange, allowIgnore = false }) {
  const opts = accounts.filter((a) => a.type === type);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      <option value="">Pick a category…</option>
      {opts.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
      {allowIgnore && <option value={IGNORE}>— Not a business transaction (ignore)</option>}
    </select>
  );
}

/* ---------------- Stripe ---------------- */
function StripePendingRow({ t, accounts, onDone }) {
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState("inc_services");
  // A refund row names the original payment, not the payer — the API looks
  // the original up and hands back who is being refunded.
  const isRefund = t.gross_cents < 0;
  const [vendor, setVendor] = useState(t.payer || t.refund_of?.payer || t.description || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function act(body) {
    setBusy(true);
    setErr("");
    try {
      await api("/api/money/stripe", { method: "POST", body });
      onDone();
    } catch (e) {
      setErr(e.message || "Failed");
      setBusy(false);
    }
  }

  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-4 py-3 text-left">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-slate-800">{t.payer || t.description || t.type}</span>
          <span className="block text-xs text-slate-400">
            {t.date}
            {t.payer && t.description ? ` · ${t.description}` : ""} · fee {centsToDisplay(t.fee_cents)} · net {centsToDisplay(t.net_cents)}
          </span>
        </span>
        <span className={`shrink-0 text-sm font-semibold ${isRefund ? "text-red-600" : "text-emerald-600"}`}>{centsToDisplay(t.gross_cents)}</span>
      </button>
      {open && (
        <div className="space-y-2 px-4 pb-3">
          <div className="grid grid-cols-2 gap-2">
            {isRefund ? (
              <Field label="Books as">
                <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-600">Refunds &amp; returns — reduces profit, tax, and tithe</p>
              </Field>
            ) : (
              <Field label="Income category">
                <AccountSelect accounts={accounts} type="income" value={account} onChange={setAccount} allowIgnore />
              </Field>
            )}
            <Field label={isRefund ? "Refunded to" : "Client / payer"}>
              <input value={vendor} onChange={(e) => setVendor(e.target.value)} className={inputCls} />
            </Field>
          </div>
          {isRefund && (
            <p className="text-xs text-slate-500">
              The original payment stays on the books as the gross receipt it was; this entry takes the refunded amount back out.
              {t.refund_of ? " Linked to the original Stripe payment." : ""}
            </p>
          )}
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex gap-2">
            <button
              onClick={() =>
                act(
                  account === IGNORE
                    ? { action: "ignore", id: t.id }
                    : isRefund
                      ? { action: "post", id: t.id, vendor }
                      : { action: "post", id: t.id, account_id: account, vendor },
                )
              }
              disabled={busy}
              className={`flex-1 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-50 ${account === IGNORE ? "bg-slate-500" : isRefund ? "bg-navy-700" : "bg-emerald-600"}`}
            >
              {account === IGNORE
                ? "Ignore — leave it out of the books"
                : isRefund
                  ? `↩️ Record refund (${centsToDisplay(Math.abs(t.gross_cents))})`
                  : `✓ Add to books (${centsToDisplay(t.gross_cents)} + fee)`}
            </button>
            <button
              onClick={() => act({ action: "ignore", id: t.id })}
              disabled={busy}
              className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-500"
            >
              Ignore
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// The five figures Stripe's own overview shows, so the two screens can be put
// side by side. Reconciling the books in September 2026 took four days to
// establish that income was overstated by $5,639 — and every number needed was
// already synced. It just could not be seen.
function StripeTotals({ totals }) {
  if (!totals) return null;
  const { scoped, all_time: all, year, in_transit_cents: inTransit } = totals;
  const rows = [
    ["Gross volume", "gross_cents", scoped.charges ? `${scoped.charges} payment${scoped.charges === 1 ? "" : "s"}` : null],
    ["Refunded", "refunded_cents", null],
    ["Processing fees", "processing_fee_cents", null],
    ["Net volume", "net_volume_cents", "matches Stripe’s overview"],
    ["Stripe service fees", "service_fee_cents", null],
    ["Paid out to the bank", "paid_out_cents", scoped.payouts ? `${scoped.payouts} deposit${scoped.payouts === 1 ? "" : "s"}` : null],
  ];
  const pulled = scoped.pulled_back_cents || all.pulled_back_cents;
  // All-time is a superset of the year, so concatenating the two lists reports
  // the same row twice. Merge by type — the operator needs to know which types
  // are uncounted, not how many scopes noticed them.
  const unclassified = Object.values(
    [...(scoped.unclassified || []), ...(all.unclassified || [])].reduce((acc, u) => {
      acc[u.type] ??= { type: u.type, count: 0, gross_cents: 0 };
      if (acc[u.type].count < u.count) acc[u.type] = { ...u };
      return acc;
    }, {}),
  );

  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-baseline justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Stripe totals</p>
        <p className="text-[11px] text-slate-400">{year} · all time</p>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, key, note]) => (
            <tr key={key} className="border-t border-slate-100 first:border-0">
              <td className="py-1.5 pr-2">
                <span className={key === "net_volume_cents" ? "font-semibold text-slate-700" : "text-slate-600"}>{label}</span>
                {note && <span className="block text-[10px] leading-tight text-slate-400">{note}</span>}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums font-medium text-slate-700">{signed(scoped[key])}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-400">{signed(all[key])}</td>
            </tr>
          ))}
          {pulled > 0 && (
            <tr className="border-t border-slate-100">
              <td className="py-1.5 pr-2">
                <span className="text-slate-600">Pulled back from the bank</span>
                <span className="block text-[10px] leading-tight text-amber-700">
                  {scoped.pull_backs || all.pull_backs} withdrawal
                  {(scoped.pull_backs || all.pull_backs) === 1 ? "" : "s"} — Stripe covering a negative balance, not a payout
                </span>
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums font-medium text-slate-700">{signed(scoped.pulled_back_cents)}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-400">{signed(all.pulled_back_cents)}</td>
            </tr>
          )}
          <tr className="border-t border-slate-200">
            <td className="py-1.5 pr-2 text-slate-600">
              Still at Stripe
              <span className="block text-[10px] leading-tight text-slate-400">collected, not yet paid out — never a year figure</span>
            </td>
            <td className="py-1.5 pr-3 text-right tabular-nums text-slate-400">—</td>
            <td className="py-1.5 text-right tabular-nums font-medium text-slate-700">{signed(inTransit)}</td>
          </tr>
        </tbody>
      </table>
      {unclassified.length > 0 && (
        <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
          {unclassified.length} Stripe transaction type{unclassified.length === 1 ? " above is" : "s above are"} not counted in any line:{" "}
          {unclassified.map((u) => `${u.type} (${signed(u.gross_cents)})`).join(", ")}. Listed rather than hidden, so these totals
          still account for every row.
        </p>
      )}
      <p className="mt-2 text-[11px] leading-snug text-slate-400">
        Paid out is money already counted as income when the client paid — it is a transfer, not revenue. If your income looks
        close to gross volume plus paid out, that is the double count.
      </p>
    </section>
  );
}

function StripeTab({ accounts }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api("/api/money/stripe").then(setData).catch(() => setData({ configured: false, pending: [], recent: [], activity: [] }));
  useEffect(() => {
    load();
  }, []);

  if (!data) return <p className="text-center text-sm text-slate-400">Loading…</p>;

  if (!data.configured && !data.demo)
    return (
      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-700">💳 Connect Stripe (read-only)</p>
        <p className="mt-2 text-sm text-slate-500">
          In the Stripe dashboard create a <strong>restricted key</strong> with read-only access to Balance
          transactions, Charges, and Payouts. Then add it as the <code>STRIPE_API_KEY</code> secret on the Cloudflare
          Pages project (Production + Preview) and the cron worker. Payments start appearing here for one-tap posting
          to the books — this app can never move money.
        </p>
      </section>
    );

  async function syncNow() {
    setBusy(true);
    setNote("");
    try {
      const r = await api("/api/money/stripe", { method: "POST", body: { action: "sync" } });
      const pulled = r.inserted ? `Pulled ${r.inserted} new transaction${r.inserted > 1 ? "s" : ""}.` : "Nothing new at Stripe.";
      // A step after the pull can fail on its own without failing the sync. Say
      // so: "Pulled 3" beside a silently skipped fee booking is the same defect
      // as the bare "Request Failed" this replaced, just quieter.
      setNote([pulled, ...(r.warnings || []).map((w) => `⚠️ ${w.message}`)].join(" "));
      load();
    } catch (e) {
      setNote(`⚠️ ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {data.demo ? (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">💳 Stripe (live app)</p>
          <p className="mt-1 text-xs text-amber-900/80">
            Demo mode — no Stripe account is connected, and none can be from here. In the live app, a read-only
            Stripe key syncs every payment hourly; each one posts to the books with its fee in a tap, refunds book as
            returns, payouts reconcile against the bank automatically, and Stripe's own service fees become expenses
            on arrival. The sample rows below show the review flow.
          </p>
        </section>
      ) : (
        <section className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-700">💳 Stripe connected</p>
            <p className="text-xs text-slate-400">{data.last_sync ? `Last sync ${data.last_sync} UTC · auto-syncs daily, 8am` : "Auto-syncs daily, 8am"}</p>
            {data.expand_degraded && (
              <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <span className="font-semibold">Syncing, but without payer names.</span> Your Stripe key cannot read customers, so new
                payments will not say who paid. Granting <span className="font-semibold">Customers → Read</span> on the key restores it;
                the next sync clears this notice on its own. Stripe said: “{data.expand_degraded}”
              </p>
            )}
            {note && <p className="mt-1 text-xs text-slate-600">{note}</p>}
          </div>
          <button onClick={syncNow} disabled={busy} className="shrink-0 rounded-xl bg-navy-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? "Syncing…" : "Sync now"}
          </button>
        </section>
      )}

      <section>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">To review ({data.pending.length})</p>
        {data.pending.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            Nothing waiting — new payments land here.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {data.pending.map((t) => (
              <StripePendingRow key={t.id} t={t} accounts={accounts} onDone={load} />
            ))}
          </div>
        )}
      </section>

      <StripeTotals totals={data.totals} />

      {(data.activity?.length ?? 0) > 0 && (
        <section>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {data.year} activity ({data.activity.length}
            {data.activity_truncated ? "+" : ""})
          </p>
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {data.activity.map((t) => (
              <div key={t.id} className="flex items-center gap-2 px-4 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate text-slate-600">
                  {t.date} · {t.type}
                  {t.description ? ` · ${t.description}` : ""}
                </span>
                <span className={`shrink-0 text-xs font-semibold uppercase ${t.status === "posted" ? "text-emerald-600" : "text-slate-400"}`}>
                  {t.status}
                </span>
                <span className="shrink-0 font-medium text-slate-700">{signed(t.gross_cents)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

/* ---------------- Bank import ---------------- */
function BankPendingRow({ t, accounts, onDone }) {
  const isExpense = t.amount_cents < 0;
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(isExpense ? "expense" : "income");
  const [account, setAccount] = useState(t.suggested_account_id || "");
  const [vendor, setVendor] = useState(t.suggested_vendor || "");
  const [pct, setPct] = useState("100");
  const [candidates, setCandidates] = useState(null); // null = not asked
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function act(body) {
    setBusy(true);
    setErr("");
    try {
      await api("/api/money/bank", { method: "POST", body: { id: t.id, ...body } });
      onDone();
    } catch (e) {
      setErr(e.message || "Failed");
      setBusy(false);
    }
  }

  async function loadCandidates() {
    const d = await api(`/api/money/bank?candidates_for=${t.id}`);
    setCandidates(d.candidates || []);
  }

  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-4 py-3 text-left">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-slate-800">{t.description}</span>
          <span className="block text-xs text-slate-400">
            {t.posted_date} · {t.source_label}
            {t.suggested_account_id ? " · suggested ✓" : ""}
            {t.match_hint > 0 ? <span className="ml-1 font-semibold text-amber-600">⚑ may already be in the books — use Match</span> : null}
          </span>
        </span>
        <span className={`shrink-0 text-sm font-semibold ${isExpense ? "text-red-600" : "text-emerald-600"}`}>{signed(t.amount_cents)}</span>
      </button>
      {open && (
        <div className="space-y-2 px-4 pb-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Type">
              <select value={type} onChange={(e) => { setType(e.target.value); setAccount(""); }} className={inputCls}>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
                <option value="owner_contribution">Self-funded (my money in)</option>
                <option value="owner_draw">Owner draw (my money out)</option>
                <option value={IGNORE}>Not a business transaction (ignore)</option>
              </select>
            </Field>
            {type === "expense" || type === "income" ? (
              <Field label="Category">
                <AccountSelect accounts={accounts} type={type} value={account} onChange={setAccount} />
              </Field>
            ) : type === IGNORE ? (
              <Field label="Category">
                <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-snug text-slate-500">
                  None — this line stays off the books entirely. Use it for money that is not business activity, such as a bank's
                  account-verification deposits.
                </p>
              </Field>
            ) : (
              <Field label="Category">
                <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-500">Owner's equity — not income or expense</p>
              </Field>
            )}
          </div>
          {(type === "expense" || type === "income") && (
            <div className="grid grid-cols-[1fr_5.5rem] gap-2">
              <Field label={type === "expense" ? "Vendor" : "Client / payer"}>
                <input value={vendor} onChange={(e) => setVendor(e.target.value)} className={inputCls} placeholder="Remembered for next month's import" />
              </Field>
              <Field label="Business %">
                <input value={pct} onChange={(e) => setPct(e.target.value)} inputMode="numeric" className={inputCls} />
              </Field>
            </div>
          )}
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex gap-2">
            <button
              onClick={() =>
                act(
                  type === IGNORE
                    ? { action: "ignore" }
                    : { action: "confirm", account_id: account, vendor, type, business_use_pct: Number(pct) || 100 },
                )
              }
              disabled={busy || (!account && (type === "expense" || type === "income"))}
              className={`flex-1 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-50 ${type === IGNORE ? "bg-slate-500" : "bg-emerald-600"}`}
            >
              {type === IGNORE ? "Ignore — leave it out of the books" : "✓ Add to books"}
            </button>
            <button
              onClick={() => (candidates === null ? loadCandidates() : setCandidates(null))}
              disabled={busy}
              className="rounded-xl border border-navy-600/30 px-3 py-2.5 text-sm font-semibold text-navy-700"
            >
              Match…
            </button>
            <button onClick={() => act({ action: "ignore" })} disabled={busy} className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-500">
              Ignore
            </button>
          </div>
          {candidates !== null && (
            <div className="rounded-xl bg-slate-50 p-2">
              {candidates.length === 0 && <p className="px-2 py-1 text-xs text-slate-500">No matching entries (± 5 days, same amount).</p>}
              {candidates.map((c) => (
                <button
                  key={c.id}
                  onClick={() => act({ action: "match", transaction_id: c.id })}
                  disabled={busy}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-white"
                >
                  <span className="min-w-0 flex-1 truncate">
                    {c.date} · {c.vendor || c.memo || c.account_name}
                  </span>
                  <span className="shrink-0 font-medium">{centsToDisplay(c.amount_cents)}</span>
                </button>
              ))}
              <p className="px-2 pt-1 text-[11px] text-slate-400">Matching links this bank line to an entry already in the books (its receipt stays attached) — no duplicate.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImportTab({ accounts }) {
  const fileRef = useRef(null);
  const [sourceLabel, setSourceLabel] = useState(() => localStorage.getItem("bt_import_source") || "VIA Checking");
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api("/api/money/bank").then(setData).catch(() => setData({ pending: [], counts: {} }));
  useEffect(() => {
    load();
  }, []);

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setNote("");
    localStorage.setItem("bt_import_source", sourceLabel);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("source_label", sourceLabel);
      const res = await fetch("/api/money/import", { method: "POST", body: fd, credentials: "include" });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Import failed");
      const parts = [`Imported ${d.imported} new line${d.imported === 1 ? "" : "s"}`];
      if (d.duplicates) parts.push(`${d.duplicates} already seen`);
      if (d.superseded) parts.push(`${d.superseded} email alert${d.superseded === 1 ? "" : "s"} superseded by the statement`);
      if (d.auto_matched) parts.push(`${d.auto_matched} matched to money already booked`);
      if (d.auto_completed) parts.push(`${d.auto_completed} auto-completed from your learned rules`);
      setNote(`${parts.join(" · ")}. ${d.needs_review ? `${d.needs_review} need${d.needs_review === 1 ? "s" : ""} your review below.` : "Nothing needs review — all reconciled ✓"}`);
      load();
    } catch (e2) {
      setNote(`⚠️ ${e2.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {data?.balances?.length > 0 && (
        <section className="rounded-2xl bg-white p-4 shadow-sm">
          {data.balances.map((b) => (
            <div key={b.account} className="flex items-baseline justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{b.account}</p>
                <p className="text-2xl font-semibold text-navy-700">{signed(b.amount_cents)}</p>
              </div>
              <p className="text-xs text-slate-400">as of {b.as_of || "last import"}</p>
            </div>
          ))}
          {data?.stripe_in_transit_cents > 0 && (
            <div className="mt-2 flex items-baseline justify-between border-t border-slate-100 pt-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">In transit from Stripe</p>
                <p className="text-lg font-semibold text-slate-600">{signed(data.stripe_in_transit_cents)}</p>
              </div>
              <p className="text-xs text-slate-400">already booked · payout pending</p>
            </div>
          )}
          <p className="mt-1 text-[11px] text-slate-400">From the last statement import — not a live feed.</p>
        </section>
      )}
      <section className="space-y-2 rounded-2xl bg-white p-4 shadow-sm">
        <p className="text-sm font-semibold text-slate-700">🏦 Import a statement</p>
        <p className="text-xs text-slate-500">
          Download transactions from VIA online banking — the QuickBooks (.qbo) or Quicken (.qfx) option — or a CSV
          from any bank/card, and upload it here. Lines you've already imported are skipped automatically, so
          overlapping exports are safe.
        </p>
        <Field label="Account name (shows as payment method)">
          <input value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)} className={inputCls} />
        </Field>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="w-full rounded-xl bg-navy-700 py-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? "Importing…" : "📄 Choose statement file"}
        </button>
        <input ref={fileRef} type="file" accept=".csv,.qbo,.qfx,.ofx,text/csv" hidden onChange={onPick} />
        {note && <p className="text-xs text-slate-600">{note}</p>}
      </section>

      {data && (
        <section>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
            To review ({data.pending.length})
            {data.counts.imported || data.counts.matched ? ` · ${(data.counts.imported || 0) + (data.counts.matched || 0)} done` : ""}
          </p>
          {data.pending.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
              All caught up — import a statement above.
            </div>
          ) : (
            <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
              {data.pending.map((t) => (
                <BankPendingRow key={t.id} t={t} accounts={accounts} onDone={load} />
              ))}
            </div>
          )}
        </section>
      )}
    </>
  );
}

/* ---------------- Invoices ---------------- */
function InvoiceForm({ onDone, onCancel }) {
  const [contact, setContact] = useState(null);
  const [issueDate, setIssueDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ description: "", qty: "1", price: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const total = lines.reduce((s, l) => s + (dollarsToCents(l.price) || 0) * (Number(l.qty) || 1), 0);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await api("/api/invoices", {
        method: "POST",
        body: {
          contact_id: contact?.id || null,
          issue_date: issueDate,
          due_date: dueDate || null,
          notes,
          lines: lines
            .filter((l) => l.description.trim())
            .map((l, idx) => ({ description: l.description, qty: Number(l.qty) || 1, unit_cents: dollarsToCents(l.price) || 0, sort: idx })),
        },
      });
      navigate(`/invoices/${r.id}`);
    } catch (e2) {
      setErr(e2.message || "Couldn't create the invoice");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">🧾 New invoice</p>
      <Field label="Bill to">
        <ContactPicker value={contact} onChange={setContact} placeholder="Client (name or company)…" allowCreate />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Issue date">
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Due date">
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
        </Field>
      </div>
      <Field label="Line items">
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_3.5rem_5.5rem] gap-2">
              <input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Description" className={inputCls} />
              <input value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} inputMode="decimal" placeholder="Qty" className={inputCls} />
              <input value={l.price} onChange={(e) => setLine(i, "price", e.target.value)} inputMode="decimal" placeholder="$ each" className={inputCls} />
            </div>
          ))}
          <button type="button" onClick={() => setLines((ls) => [...ls, { description: "", qty: "1", price: "" }])} className="text-xs font-semibold text-navy-700">
            + Add line
          </button>
        </div>
      </Field>
      <Field label="Notes / terms (shown on the invoice)">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} placeholder="e.g. Net 30. Make checks payable to AI Engines." />
      </Field>
      <p className="text-right text-sm font-semibold text-navy-700">Total {centsToDisplay(total)}</p>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
          Cancel
        </button>
        <button disabled={busy} className="flex-1 rounded-xl bg-navy-700 py-3 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? "Creating…" : "Create invoice"}
        </button>
      </div>
    </form>
  );
}

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-500",
  sent: "bg-navy-700/10 text-navy-700",
  paid: "bg-emerald-100 text-emerald-700",
  void: "bg-slate-100 text-slate-400 line-through",
};

function InvoicesTab({ newNonce = 0 }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api("/api/invoices").then((d) => setInvoices(d.invoices)).catch(() => setInvoices([]));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (newNonce) setAdding(true);
  }, [newNonce]);

  if (invoices === null) return <p className="text-center text-sm text-slate-400">Loading…</p>;

  const outstanding = invoices.filter((i) => i.status === "sent").reduce((s, i) => s + i.total_cents, 0);
  const overdue = invoices.filter((i) => i.days_overdue > 0).reduce((s, i) => s + i.total_cents, 0);

  return (
    <>
      <section className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm">
        <div>
          <p className="text-xs text-slate-400">Outstanding</p>
          <p className="text-xl font-semibold text-navy-700">
            {centsToDisplay(outstanding)}
            {overdue > 0 && <span className="ml-2 text-sm font-semibold text-red-600">({centsToDisplay(overdue)} overdue)</span>}
          </p>
        </div>
        <button onClick={() => setAdding(true)} className="rounded-xl bg-navy-700 px-4 py-2 text-sm font-semibold text-white">
          + Invoice
        </button>
      </section>

      {adding && <InvoiceForm onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />}

      {invoices.length === 0 && !adding ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-4xl">🧾</p>
          <p className="mt-3 font-medium text-slate-700">No invoices yet</p>
          <p className="mt-1 text-sm text-slate-500">Bill a client and track who owes you.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
          {invoices.map((inv) => (
            <button key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} className="flex w-full items-center gap-2 px-4 py-3 text-left">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-800">
                  {inv.number} · {inv.contact_company || inv.contact_name || "No client"}
                </span>
                <span className="block text-xs text-slate-400">
                  {inv.issue_date}
                  {inv.due_date ? ` · due ${inv.due_date}` : ""}
                  {inv.days_overdue > 0 ? ` · ${inv.days_overdue}d overdue` : ""}
                </span>
              </span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_STYLE[inv.status] || ""}`}>{inv.status}</span>
              <span className={`shrink-0 text-sm font-semibold ${inv.days_overdue > 0 ? "text-red-600" : "text-slate-700"}`}>
                {centsToDisplay(inv.total_cents)}
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

export default function Money() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "stripe");
  const [newNonce, setNewNonce] = useState(0);
  const accounts = useAccounts();

  // Same-route deep links (the Add menu): re-sync tab on every navigation and
  // consume ?new=1 one-shot — the Crm.jsx pattern.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get("tab");
    if (t && TABS.some((x) => x.value === t)) setTab(t);
    if (p.get("new") === "1") {
      setTab("invoices");
      setNewNonce((n) => n + 1);
      p.delete("new");
      setParams(p, { replace: true });
    }
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Shell title="Money">
      <Segmented options={TABS} value={tab} onChange={setTab} />
      {tab === "stripe" && <StripeTab accounts={accounts} />}
      {tab === "import" && <ImportTab accounts={accounts} />}
      {tab === "invoices" && <InvoicesTab newNonce={newNonce} />}
    </Shell>
  );
}
